import { ConsumableLine, ConsumablePricingContext, ConsumablePricingResult } from '../consumable-models';
import { BaseConsumableRule } from '../consumable-rule-base';

/**
 * Rule: Full Pack Pricing
 * 
 * Handles cases where the full pack is dispensed.
 * Some products may have special pricing when the full pack is dispensed.
 * 
 * This rule runs after partial pack dispensing and can override pricing
 * if the quantity matches the standard pack size exactly.
 * Note: VAT is applied separately after pricing calculations
 */
export class RuleFullPackPricing extends BaseConsumableRule {
    name = 'Full Pack Pricing';
    priority = 2; // Run after partial pack dispensing
    
    applies(line: ConsumableLine, ctx: ConsumablePricingContext): boolean {
        // Apply if quantity exactly matches standard pack size
        return line.codeType === 'consumable' && 
               line.standardPacksize > 0 &&
               line.quantity === line.standardPacksize;
    }
    
    apply(line: ConsumableLine, ctx: ConsumablePricingContext): ConsumablePricingResult {
        // For full pack, use the single exit price directly
        let packPrice = line.singleExitPrice || 0;
        
        // Apply doctor markup if not a network doctor (VAT is applied separately)
        if (!ctx.isNetworkDoctor && ctx.modifier) {
            packPrice = this.applyMarkup(packPrice, ctx.modifier);
        }
        
        const trace = [
            `Full pack pricing: R${line.singleExitPrice} (pack of ${line.standardPacksize})`,
            ...(ctx.modifier && !ctx.isNetworkDoctor 
                ? [`After markup (${ctx.modifier}%): R${packPrice.toFixed(2)}`]
                : [`Pack price: R${packPrice.toFixed(2)}`]),
            `Total: R${packPrice.toFixed(2)} (VAT will be applied separately)`
        ];
        
        return {
            line: { ...line },
            calculatedUnitPrice: packPrice / (line.quantity || 1),
            calculatedTotalPrice: packPrice,
            trace,
            warnings: []
        };
    }
}

